// The walk-through bike shop's geometry (bike plan 5). Phaser-free so it loads under `node --test`.
//
// The room is ONE composed picture (bg_bike_shop_hall, 5240x941 = [left 1672][middle 1672][right 1896], see
// scripts/compose_bike_shop.py), served as two 2620-px tiles and scaled to height 720 the way the dealership is —
// world x = srcX * SHOP_SCALE.
//
// Traced 2026-10-25 off the composed room at world scale (t9 check_shop.py: a 100-px grid preview, and the dark runs
// along world rows 360/385 for the podiums, the counter and the door); retraced the same day after the owner's restyle
// (a modern white bike studio, the panels' rows normalised onto one set of lines by compose_bike_shop.py):
//   the plinth   its back edge (wall meets the light grey top) y ≈ 407, its front lip y ≈ 432, its plywood front down
//                to the floor at y ≈ 497; the floor's two black stripes at y ≈ 539…555
//   podiums      x 310…592 (city bikes, deep end), top 348…372; x 1910…2165 (basket bikes), top 361…380
//   signboards   x 1547…1800, y 133…215 (over the city bikes' near end) and x 3122…3398, y 130…213 (baskets)
//   the counter  x 3494…3803, the shopkeeper behind it at ≈ 3680; the street door 3813…3995
// So, as at the dealership, you walk IN at the right edge, by the counter, and browse leftwards: the basket bikes
// first, cheapest by the door, then the city bikes, each section's Specjalny deepest on its podium.

export const SHOP_SRC_W = 5240;
export const SHOP_SRC_H = 941;
export const SHOP_SCALE = 720 / SHOP_SRC_H;                              // 0.76514
export const SHOP_WORLD_WIDTH = Math.round(SHOP_SRC_W * SHOP_SCALE);    // 4009

export function shopX(srcX) { return Math.round(srcX * SHOP_SCALE); }

// The two served halves (each 2620 src px, under the 4096-px texture limit), in world px.
const TILE0_W = shopX(SHOP_SRC_W / 2);
export const TILES = [
  { key: 'bg_bike_shop_0', x: 0, w: TILE0_W },
  { key: 'bg_bike_shop_1', x: TILE0_W, w: SHOP_WORLD_WIDTH - TILE0_W },
];

// The walker's sprite-centre line: her feet at FLOOR_Y + 120 = 600, on the floor a step in front of the plinth
// (whose foot is at ≈ 497).
export const FLOOR_Y = 480;
// Where a bike's wheels stand: the middle of the plinth's top (407…432).
export const DISPLAY_Y = 420;
// …and on a podium's top: inside both painted tops (348…372 and 361…380).
export const PODIUM_Y = 365;

// A parked bike's drawn height, and how close you stand to a bike for its card (the dealer's NEAR is 120).
export const BIKE_H = 130;
export const NEAR_BIKE = 125;

// Left to right. Each section's Specjalny stands on its podium's centre (458, 2034); the others are spaced evenly
// from it, the blue bikes 260 apart (the blue side view, 1.84 x BIKE_H, plus 20 — they end at 1878, just short of
// the basket podium), the basket bikes 266 apart, up to 3364 (its back wheel well clear of the counter at 3494).
const BLUE = ['plomienie', 'ocean', 'wyscigowy', 'grafitowy', 'wisniowy', 'niebieski'];
const PINK = ['jednorozec', 'arbuz', 'biedronka', 'cytrynowy', 'mietowy', 'rozowy'];
const row = (type, designs, x0, gap) => designs.map((design, i) => ({
  type, design, x: x0 + i * gap, y: i === 0 ? PODIUM_Y : DISPLAY_Y, podium: i === 0,
}));
export const SPOTS = [...row('blue', BLUE, 458, 260), ...row('pink', PINK, 2034, 266)];

// The section names, drawn by the game on the painted blank boards.
export const SECTIONS = [
  { text: 'Rowery miejskie', x: 1673, y: 174 },
  { text: 'Rowerki z koszykiem', x: 3260, y: 171 },
];

// Where you stand to buy grease: in front of the shopkeeper.
export const COUNTER = { x: 3650, y: FLOOR_Y };

// Walking past this x steps out onto the street (the doormat in front of the door). The walker's own bound is
// SHOP_WORLD_WIDTH − 60 = 3949, so the band is reachable.
export const EXIT_ZONE = { xMin: 3800 };

// Where you land coming in from the street: just inside the door, at the counter, clear of the trigger.
export const SPAWN = { x: 3770, y: 580 };

export function nearestSpot(x) {
  let best = null;
  for (const s of SPOTS) if (!best || Math.abs(x - s.x) < Math.abs(x - best.x)) best = s;
  return best && Math.abs(x - best.x) <= NEAR_BIKE ? best : null;
}

// The bike drawn at a world point (a click or a tap), or null: each bike's box is its side view, LONGEST wide
// (shorter than the gaps, so boxes never overlap) and BIKE_H tall, standing on its spot. Not the sprite's
// getBounds(): its trimmed atlas frames keep their ~1250-px source size, so that box is ~600 px wide.
const LONGEST = BIKE_H * 1.84;
export function spotAt(x, y) {
  return SPOTS.find((s) => Math.abs(x - s.x) < LONGEST / 2 && y <= s.y && y >= s.y - BIKE_H) ?? null;
}

// Where you stand on arrival. Phaser keeps a scene's last start data when the next start passes none (the street
// door does), so the spawn BootScene resumes after a reload would otherwise come back on every later entry: it is
// read once and deleted, as OutsideScene's takeOnce does.
export function arrivalSpawn(data) {
  const out = { x: data?.spawnX ?? SPAWN.x, y: data?.spawnY ?? SPAWN.y };
  if (data) for (const k of ['spawnX', 'spawnY', 'spawnFacing']) delete data[k];
  return out;
}

export function atCounter(x) { return Math.abs(x - COUNTER.x) <= NEAR_BIKE; }

export function inExitZone(x) { return x >= EXIT_ZONE.xMin; }
